import { useState, useEffect } from "react";
import axios from 'axios';
import config from "../../Common/Configurations/APIConfig";
import { handleSuccess, handleError } from "../../Common/Layout/CustomAlerts";
import { Link } from "react-router-dom";
import Header from "../../Common/Layout/Header";
import SideBar from "../../Common/Layout/Sidebar";

const socialMediaOptions = [
    { platform: "Facebook", iconClass: "ri-facebook-fill", urlPrefix: "https://www.facebook.com/" },
    { platform: "Twitter", iconClass: "ri-twitter-fill", urlPrefix: "https://www.twitter.com/" },
    { platform: "LinkedIn", iconClass: "ri-linkedin-fill", urlPrefix: "https://www.linkedin.com/" },
    { platform: "Instagram", iconClass: "ri-instagram-fill", urlPrefix: "https://www.instagram.com/" },
    { platform: "YouTube", iconClass: "ri-youtube-fill", urlPrefix: "https://www.youtube.com/" },
    { platform: "Pinterest", iconClass: "ri-pinterest-fill", urlPrefix: "https://www.pinterest.com/" },
];

const initialValues = {
    footerId: "",
    backgroundColor: "#000000", // default to black
    companyName: "",
    tagline: "",
    address: "",
    email: "",
    phone: "",
    socialLinks: []
};

export default function FooterSection() {
    const [values, setValues] = useState(initialValues);
    const [errors, setErrors] = useState({});
    const [isEditMode, setIsEditMode] = useState(false);
    const [originalData, setOriginalData] = useState(null);
    const headerconfig = {
        headers: {
            Authorization: "Bearer " + localStorage.getItem("userToken")
        }
    };

    useEffect(() => {
        getFooter();
    }, []);

    const getFooter = () => {
        axios.get(config.APIACTIVATEURL + config.GETFOOTER, headerconfig)
            .then(res => {
                if (res.data.data.length > 0) {
                    const data = res.data.data[0];
                    // Ensure socialLinks is an array and has at least empty objects
                    if (!data.socialLinks || data.socialLinks.length === 0) {
                        data.socialLinks = [createEmptySocialLink()];
                    }
                    setValues(data);
                    setOriginalData(data);
                }
            })
            .catch(err => console.log(err));
    };

    const createEmptySocialLink = () => ({
        socialLinkId: "00000000-0000-0000-0000-000000000000",
        footerId: "",
        platform: "",
        url: "",
        iconClass: ""
    });

    const addSocialLink = () => {
        setValues({
            ...values,
            socialLinks: [...values.socialLinks, createEmptySocialLink()]
        });
    };

    const removeSocialLink = (index) => {
        const updatedLinks = [...values.socialLinks];
        updatedLinks.splice(index, 1);
        setValues({
            ...values,
            socialLinks: updatedLinks.length > 0 ? updatedLinks : [createEmptySocialLink()]
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setValues({ ...values, [name]: value });
    };

    const handleSocialLinkChange = (index, field, value) => {
        const updatedSocialLinks = [...values.socialLinks];

        // If platform changes, update iconClass automatically
        if (field === 'platform') {
            const selectedPlatform = socialMediaOptions.find(opt => opt.platform === value);
            if (selectedPlatform) {
                updatedSocialLinks[index].iconClass = selectedPlatform.iconClass;
                // Auto-fill URL prefix if URL is empty
                if (!updatedSocialLinks[index].url) {
                    updatedSocialLinks[index].url = selectedPlatform.urlPrefix;
                }
            }
        }

        updatedSocialLinks[index][field] = value;
        setValues({ ...values, socialLinks: updatedSocialLinks });
    };

    const validate = () => {
        let temp = {};
        temp.companyName = values.companyName ? true : false;
        temp.tagline = values.tagline ? true : false;
        temp.email = values.email ? true : false;
        setErrors(temp);
        return Object.values(temp).every(x => x === true);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            const updatedSocialLinks = values.socialLinks.map(link => ({
                ...link,
                footerId: values.footerId // Ensure each link has the correct footerId
            }));

            const payload = {
                ...values,
                socialLinks: updatedSocialLinks,
                lastUpdated: new Date().toISOString()
            };

            axios.put(
                `${config.APIACTIVATEURL}${config.UPDATEFOOTER}/${values.footerId}`,
                payload,
                headerconfig
            )
                .then(res => {
                    if (res.data.statusCode === 200) {
                        handleSuccess("Footer Updated Successfully");
                        setIsEditMode(false);
                        getFooter();
                    } else {
                        handleError(res.data.message);
                    }
                })
                .catch(err => {
                    console.error(err);
                    handleError("Update failed");
                });
        }
    };

    const handleCancel = () => {
        setValues(originalData);
        setIsEditMode(false);
    };

    const applyErrorClass = (field) =>
        field in errors && errors[field] === false ? " is-invalid" : "";

    return (
        <div id="layout-wrapper">
            <Header />
            <SideBar />
            {/* Use the same structure as your working screen */}
            <div className="body-wrapper pt-5">
                    {/* Preview Section */}
                    <div className="layout-wrapper mb-5">
                        <footer className="custom-footer py-5 position-relative" style={{ backgroundColor: values.backgroundColor }}>
                            <div className="container">
                                <div className="row">
                                    <div className="col-lg-4 mt-4">
                                        <div>
                                            <div>
                                                <img src="/assets/images/logo3d.png" alt="logo light" height={80} width={280} />
                                            </div>
                                            <div className="mt-4">
                                                <p className="text-white">{values.companyName}</p>
                                                <p className="text-white">{values.tagline}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-lg-7 ms-lg-auto">
                                        <div className="row">
                                            <div className="col-sm-4 mt-4">
                                                {/* Empty column for spacing */}
                                            </div>
                                            <div className="col-sm-3 mt-4">
                                                {/* Empty column for spacing */}
                                            </div>
                                            <div className="col-sm-5 mt-4">
                                                <h5 className="text-white mb-0">Contact</h5>
                                                <div className="text-white mt-3">
                                                    <ul className="list-unstyled ff-secondary footer-list fs-3">
                                                        {values.email && <li className="p-1">Email : {values.email}</li>}
                                                        {values.phone && <li className="p-1">Phone : {values.phone}</li>}
                                                    </ul>
                                                </div>
                                                <div className="text-sm-end mt-3 mt-sm-0">
                                                    <ul className="list-inline mb-0 footer-social-link">
                                                        {values.socialLinks.map((link, index) => (
                                                            link.url && (
                                                                <li className="list-inline-item" key={index}>
                                                                    <Link to={link.url} target="_blank" className="avatar-xs d-block">
                                                                        <div className="avatar-title rounded-circle">
                                                                            <i className={link.iconClass || "ri-share-fill"} />
                                                                        </div>
                                                                    </Link>
                                                                </li>
                                                            )
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </footer>
                    </div>
                    <hr style={{ border: "3px solid black", margin: "40px 0" }} />
                    
                    <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header">
                                    <h4 className="card-title">Footer Section Settings</h4>
                                </div>
                                <div className="card-body">
                                    <form onSubmit={handleSubmit}>
                                        <div className="row">
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">Company Name</label>
                                                <input
                                                    type="text"
                                                    name="companyName"
                                                    value={values.companyName}
                                                    onChange={handleInputChange}
                                                    className={`form-control${applyErrorClass("companyName")}`}
                                                    readOnly={!isEditMode}
                                                />
                                                {errors.companyName === false && (
                                                    <div className="invalid-feedback">Company Name is required</div>
                                                )}
                                            </div>

                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">Tagline</label>
                                                <input
                                                    type="text"
                                                    name="tagline"
                                                    value={values.tagline}
                                                    onChange={handleInputChange}
                                                    className={`form-control${applyErrorClass("tagline")}`}
                                                    readOnly={!isEditMode}
                                                />
                                                {errors.tagline === false && (
                                                    <div className="invalid-feedback">Tagline is required</div>
                                                )}
                                            </div>

                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">Address</label>
                                                <input
                                                    type="text"
                                                    name="address"
                                                    value={values.address}
                                                    onChange={handleInputChange}
                                                    className="form-control"
                                                    readOnly={!isEditMode}
                                                />
                                            </div>

                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">Email</label>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={values.email}
                                                    onChange={handleInputChange}
                                                    className={`form-control${applyErrorClass("email")}`}
                                                    readOnly={!isEditMode}
                                                />
                                                {errors.email === false && (
                                                    <div className="invalid-feedback">Email is required</div>
                                                )}
                                            </div>

                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">Phone</label>
                                                <input
                                                    type="text"
                                                    name="phone"
                                                    value={values.phone}
                                                    onChange={handleInputChange}
                                                    className="form-control"
                                                    readOnly={!isEditMode}
                                                />
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">Background Color</label>
                                                <input
                                                    type="color"
                                                    name="backgroundColor"
                                                    value={values.backgroundColor}
                                                    onChange={handleInputChange}
                                                    className="form-control form-control-color"
                                                    disabled={!isEditMode}
                                                    title="Choose your color"
                                                />
                                            </div>


                                            {/* Social Links Section */}
                                            <div className="col-12 mb-3">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <label className="form-label">Social Links</label>
                                                    {isEditMode && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-primary"
                                                            onClick={addSocialLink}
                                                        >
                                                            + Add Social Link
                                                        </button>
                                                    )}
                                                </div>

                                                {values.socialLinks.map((link, index) => (
                                                    <div className="row mb-2 align-items-center" key={index}>
                                                        <div className="col-md-4 mb-2">
                                                            <select
                                                                value={link.platform}
                                                                onChange={(e) => handleSocialLinkChange(index, 'platform', e.target.value)}
                                                                className="form-control"
                                                                disabled={!isEditMode}
                                                            >
                                                                <option value="">Select Platform</option>
                                                                {socialMediaOptions.map((option, i) => (
                                                                    <option key={i} value={option.platform}>
                                                                        {option.platform}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        <div className="col-md-6 mb-2">
                                                            <input
                                                                type="url"
                                                                value={link.url}
                                                                onChange={(e) => handleSocialLinkChange(index, 'url', e.target.value)}
                                                                className="form-control"
                                                                placeholder="Full URL"
                                                                readOnly={!isEditMode}
                                                            />
                                                        </div>
                                                        <div className="col-md-1 mb-2 text-center">
                                                            {link.iconClass ? (
                                                                <i className={`${link.iconClass} fs-4`} />
                                                            ) : (
                                                                <i className="ri-share-fill fs-4" />
                                                            )}
                                                        </div>
                                                        <div className="col-md-1 mb-2">
                                                            {isEditMode && values.socialLinks.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-danger"
                                                                    onClick={() => removeSocialLink(index)}
                                                                >
                                                                    <i className="ri-delete-bin-line" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="col-12 mt-3 d-flex justify-content-end gap-2">
                                                {isEditMode ? (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary"
                                                            onClick={handleCancel}
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            type="submit"
                                                            className="btn btn-primary"
                                                        >
                                                            Save Changes
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="btn btn-warning"
                                                        onClick={() => setIsEditMode(true)}
                                                    >
                                                        Edit
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}